<template>
  <v-dialog v-model="show" max-width="820" persistent scrollable>
    <v-card rounded="lg" class="ts-card">
      <v-card-title class="d-flex justify-space-between align-center">
        <span class="ts-title">
          <TextQuote :size="18" />
          {{ $gt('Textbausteine') }}
        </span>
        <ButtonBase iconStyle iconType="cancel" color="grey" @click="close" />
      </v-card-title>

      <v-card-text class="pa-4 pt-0 ts-body">
        <!-- Liste + Suche -->
        <template v-if="!editing">
          <div class="ts-toolbar">
            <div class="ts-search">
              <Search :size="14" class="ts-search__icon" />
              <input
                ref="searchInput"
                v-model="query"
                type="text"
                class="ts-search__input"
                :placeholder="$gt('Textbaustein suchen (Titel oder Inhalt)…')"
                @keydown.enter.prevent="insertFirst"
                @keydown.escape="query ? (query = '') : close()"
              />
              <button v-if="query" type="button" class="ts-search__clear" :title="$gt('Suche leeren')" @click="query = ''">
                <X :size="13" />
              </button>
            </div>
            <ButtonBase buttonText="Neu" iconType="plus" variant="flat" size="sm" @click="startCreate" />
          </div>

          <div v-if="loading" class="ts-center">
            <v-progress-circular indeterminate size="22" width="2" color="primary" />
          </div>
          <template v-else>
            <div v-for="group in groups" :key="group.key" class="ts-group">
              <h4 v-if="groups.length > 1 || group.key === 'shared'" class="ts-group__title">
                <Users v-if="group.key === 'shared'" :size="13" />
                <User v-else :size="13" />
                <span>{{ $gt(group.label) }}</span>
                <span class="ts-group__count">{{ group.items.length }}</span>
              </h4>
              <ul class="ts-list">
                <li
                  v-for="s in group.items"
                  :key="s.id"
                  class="ts-item"
                  role="button"
                  tabindex="0"
                  :title="$gt('Klicken, um den Baustein an der Cursorposition einzufügen')"
                  @click="insert(s)"
                  @keydown.enter.prevent="insert(s)"
                >
                  <div class="ts-item__main">
                    <span class="ts-item__title">{{ s.title }}</span>
                    <span class="ts-item__preview">{{ preview(s) }}</span>
                  </div>
                  <span class="ts-item__actions" @click.stop>
                    <button
                      v-if="s.editable"
                      type="button"
                      class="ts-item__btn"
                      :title="$gt('Bearbeiten')"
                      @click="startEdit(s)"
                    >
                      <Pencil :size="14" />
                    </button>
                    <button
                      v-if="s.editable"
                      type="button"
                      class="ts-item__btn ts-item__btn--danger"
                      :title="$gt('Löschen')"
                      @click="confirmDelete(s)"
                    >
                      <Trash2 :size="14" />
                    </button>
                    <span v-else class="ts-item__lock" :title="$gt('Unternehmens-Baustein – nur Administrator/Unternehmensleiter können ihn ändern')">
                      <Lock :size="13" />
                    </span>
                  </span>
                </li>
              </ul>
            </div>
            <p v-if="filtered.length === 0" class="ts-empty">
              <template v-if="snippets.length === 0">
                {{ $gt('Noch keine Textbausteine vorhanden. Legen Sie mit „Neu“ Ihren ersten Baustein an.') }}
              </template>
              <template v-else>{{ $gt('Kein Textbaustein passt zur Suche.') }}</template>
            </p>
          </template>
        </template>

        <!-- Anlegen / Bearbeiten -->
        <div v-else class="ts-form">
          <label class="ts-form__label">{{ $gt('Titel') }}</label>
          <input
            ref="titleInput"
            v-model="form.title"
            type="text"
            class="ts-form__input"
            maxlength="120"
            :placeholder="$gt('z. B. Grußformel, Zwischenbescheid, Fristsetzung…')"
          />
          <label class="ts-form__label">{{ $gt('Inhalt') }}</label>
          <MessageComposer
            ref="composer"
            v-model="form.content"
            class="ts-form__composer"
            :placeholder="$gt('Text des Bausteins…')"
          />
          <label
            v-if="mayShare"
            class="ts-form__check"
            :class="{ 'ts-form__check--on': form.shared }"
          >
            <input v-model="form.shared" type="checkbox" class="ts-form__checkbox" />
            <Users :size="13" />
            <span>{{ $gt('Für alle Mitarbeitenden (Unternehmens-Baustein)') }}</span>
          </label>
          <p v-if="formError" class="ts-form__error">{{ formError }}</p>
        </div>
      </v-card-text>

      <v-divider />
      <v-card-actions class="d-flex align-center ma-2">
        <span v-if="!editing" class="ts-hint">{{ $gt('Klick fügt den Baustein an der Cursorposition ein.') }}</span>
        <v-spacer />
        <template v-if="editing">
          <ButtonBase buttonText="Abbrechen" iconType="cancel" @click="cancelEdit" />
          <ButtonBase buttonText="Speichern" iconType="save" variant="flat" :loading="saving" :disabled="saving" @click="save" />
        </template>
        <ButtonBase v-else buttonText="Schließen" iconType="cancel" @click="close" />
      </v-card-actions>
    </v-card>

    <YesNoDialog
      ref="deleteDialog"
      :title="$gt('Textbaustein löschen')"
      :content="deleteContent"
      type="warning"
      yesButtonText="Löschen"
      yesButtonIconType="delete"
      :onYes="doDelete"
      noButtonText="Abbrechen"
      noButtonIconType="cancel"
      :onNo="() => {}"
    />
  </v-dialog>
</template>

<script>
import { TextQuote, Search, X, Pencil, Trash2, Users, User, Lock } from "lucide-vue-next";
import ButtonBase from "@/components/buttons/ButtonBase.vue";
import YesNoDialog from "@/components/windows/dialogs/YesNoDialog.vue";
import MessageComposer from "@/components/messages/MessageComposer.vue";
import { useAuthStore } from "@/stores/authStore.ts";
import { useSnackbarStore } from "@/stores/snackbarStore.ts";
import {
  listTextSnippets, createTextSnippet, updateTextSnippet, deleteTextSnippet,
} from "@/services/textSnippetsApi";
import { htmlToPlainText, sanitizeHtml } from "@/utils/sanitizeHtml";

/**
 * Textbausteine (Inhaber-Feedback 15.08.2026, I.4): durchsuchbare Liste der
 * eigenen und der Unternehmens-Bausteine, Anlegen/Bearbeiten/Löschen direkt
 * im Fenster. Klick auf einen Baustein löst `insert` mit dem Baustein aus –
 * das Einfügen an der Cursorposition übernimmt der aufrufende Composer.
 */
export default {
  name: "TextSnippetDialog",

  components: {
    ButtonBase, YesNoDialog, MessageComposer,
    TextQuote, Search, X, Pencil, Trash2, Users, User, Lock,
  },

  emits: ["insert", "closed"],

  data() {
    return {
      show: false,
      loading: false,
      saving: false,
      snippets: [],
      query: "",
      editing: false,
      editingId: null,
      form: { title: "", content: "", shared: false },
      formError: "",
      pendingDelete: null,
    };
  },

  computed: {
    mayShare() {
      const store = useAuthStore();
      return store.isAdministrator || store.isCompanyManager || store.isSuperAdmin;
    },
    /** Suche ab dem ersten Buchstaben über Titel und (textuellen) Inhalt. */
    filtered() {
      const q = this.query.trim().toLowerCase();
      if (!q) return this.snippets;
      return this.snippets.filter((s) =>
        (s.title || "").toLowerCase().includes(q) || this.plainText(s.content).toLowerCase().includes(q));
    },
    groups() {
      const own = this.filtered.filter((s) => !s.shared);
      const shared = this.filtered.filter((s) => s.shared);
      const out = [];
      if (own.length) out.push({ key: "own", label: "Meine Bausteine", items: own });
      if (shared.length) out.push({ key: "shared", label: "Unternehmens-Bausteine (für alle Mitarbeitenden)", items: shared });
      return out;
    },
    deleteContent() {
      return this.pendingDelete
        ? `Soll der Textbaustein „${this.pendingDelete.title}“ wirklich gelöscht werden?`
        : "";
    },
  },

  methods: {
    async open() {
      this.show = true;
      this.query = "";
      this.editing = false;
      this.formError = "";
      await this.load();
      this.$nextTick(() => this.$refs.searchInput?.focus());
    },

    close() {
      this.show = false;
      this.editing = false;
      this.$emit("closed");
    },

    async load() {
      this.loading = true;
      try {
        const list = await listTextSnippets();
        this.snippets = Array.isArray(list) ? list : [];
      } catch (e) {
        useSnackbarStore().showSnackbar({ message: e?.message || "Textbausteine konnten nicht geladen werden", type: "error" });
      } finally {
        this.loading = false;
      }
    },

    /**
     * Sichtbarer Text eines Bausteins für Vorschau und Suche. Bewusst über
     * DOMParser (inertes Dokument) statt innerHTML auf einem DOM-Element:
     * Unternehmens-Bausteine sehen alle Mitarbeitenden – gespeichertes
     * Markup darf beim Rendern der Liste nichts ausführen oder laden.
     */
    plainText(html) {
      return htmlToPlainText(html);
    },

    preview(s) {
      const t = this.plainText(s.content);
      return t.length > 110 ? t.slice(0, 110) + "…" : t;
    },

    insert(s) {
      this.$emit("insert", s);
      this.close();
    },

    /** Enter im Suchfeld fügt den ersten Treffer ein. */
    insertFirst() {
      const first = this.filtered[0];
      if (first) this.insert(first);
    },

    startCreate() {
      this.editingId = null;
      this.form = { title: "", content: "", shared: false };
      this.formError = "";
      this.editing = true;
      this.$nextTick(() => this.$refs.titleInput?.focus());
    },

    startEdit(s) {
      this.editingId = s.id;
      // Der Inhalt landet per innerHTML im Editor – vorher bereinigen.
      this.form = { title: s.title, content: sanitizeHtml(s.content), shared: !!s.shared };
      this.formError = "";
      this.editing = true;
      this.$nextTick(() => this.$refs.titleInput?.focus());
    },

    cancelEdit() {
      this.editing = false;
      this.editingId = null;
      this.formError = "";
      this.$nextTick(() => this.$refs.searchInput?.focus());
    },

    async save() {
      const title = (this.form.title || "").trim();
      const content = (this.form.content || "").trim();
      if (!title) { this.formError = "Bitte einen Titel angeben."; return; }
      if (!this.plainText(content)) { this.formError = "Bitte einen Inhalt angeben."; return; }
      this.saving = true;
      this.formError = "";
      try {
        const payload = { title, content, shared: this.mayShare && this.form.shared };
        if (this.editingId) await updateTextSnippet(this.editingId, payload);
        else await createTextSnippet(payload);
        useSnackbarStore().showSnackbar({ message: "Textbaustein gespeichert", type: "success" });
        this.editing = false;
        this.editingId = null;
        await this.load();
        this.$nextTick(() => this.$refs.searchInput?.focus());
      } catch (e) {
        this.formError = e?.message || "Textbaustein konnte nicht gespeichert werden.";
      } finally {
        this.saving = false;
      }
    },

    confirmDelete(s) {
      this.pendingDelete = s;
      this.$refs.deleteDialog?.openDialog();
    },

    async doDelete() {
      const s = this.pendingDelete;
      this.pendingDelete = null;
      if (!s) return;
      try {
        await deleteTextSnippet(s.id);
        this.snippets = this.snippets.filter((x) => x.id !== s.id);
        useSnackbarStore().showSnackbar({ message: "Textbaustein gelöscht", type: "success" });
      } catch (e) {
        useSnackbarStore().showSnackbar({ message: e?.message || "Textbaustein konnte nicht gelöscht werden", type: "error" });
      }
    },
  },
};
</script>

<style scoped>
.ts-card { display: flex; flex-direction: column; }
.ts-title { display: inline-flex; align-items: center; gap: 8px; font-size: 1rem; font-weight: 600; }
.ts-body { min-height: 320px; max-height: 70vh; }

.ts-toolbar { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; }
.ts-search {
  flex: 1; display: flex; align-items: center; gap: 6px;
  border: 1px solid var(--border-grey-color); border-radius: 8px;
  padding: 6px 10px; background: #fff;
}
.ts-search:focus-within { border-color: var(--primary-color); box-shadow: 0 0 0 2px var(--primary-light-color); }
.ts-search__icon { color: var(--grey-color); flex-shrink: 0; }
.ts-search__input { flex: 1; border: none; outline: none; font-size: 0.88rem; background: transparent; font-family: inherit; }
.ts-search__clear { border: none; background: none; color: var(--grey-color); cursor: pointer; display: flex; padding: 2px; border-radius: 50%; }
.ts-search__clear:hover { background: var(--grey-light-color); }

.ts-center { display: flex; justify-content: center; padding: 30px 0; }

.ts-group + .ts-group { margin-top: 14px; }
.ts-group__title {
  display: flex; align-items: center; gap: 6px;
  font-size: 0.72rem; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase;
  color: var(--grey-color); margin: 0 0 6px 2px;
}
.ts-group__count {
  font-size: 0.68rem; background: var(--grey-light-color); border-radius: 10px; padding: 0 6px;
}
.ts-list { list-style: none; margin: 0; padding: 0; border: 1px solid #eef0f3; border-radius: 10px; overflow: hidden; }
.ts-item {
  display: flex; align-items: center; gap: 10px;
  padding: 9px 12px; cursor: pointer; transition: background 0.1s;
}
.ts-item + .ts-item { border-top: 1px solid #eef0f3; }
.ts-item:hover, .ts-item:focus-visible { background: var(--primary-light-color); outline: none; }
.ts-item__main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.ts-item__title { font-size: 0.88rem; font-weight: 600; color: var(--dark-color); }
.ts-item__preview { font-size: 0.78rem; color: var(--grey-color); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ts-item__actions { display: flex; align-items: center; gap: 2px; flex-shrink: 0; }
.ts-item__btn {
  display: flex; align-items: center; justify-content: center;
  width: 28px; height: 28px; border: none; border-radius: 6px;
  background: none; color: var(--grey-color); cursor: pointer;
}
.ts-item__btn:hover { background: rgba(0,0,0,0.06); color: var(--dark-color); }
.ts-item__btn--danger:hover { color: var(--error-color); }
.ts-item__lock { display: flex; color: var(--border-grey-color); padding: 0 6px; }
.ts-empty { text-align: center; color: var(--grey-color); font-size: 0.86rem; padding: 30px 12px; }

.ts-form { display: flex; flex-direction: column; gap: 6px; }
.ts-form__label { font-size: 0.78rem; color: var(--grey-color); margin-top: 6px; }
.ts-form__input {
  border: 1px solid var(--border-grey-color); border-radius: 8px;
  padding: 8px 10px; font-size: 0.88rem; outline: none; font-family: inherit;
}
.ts-form__input:focus { border-color: var(--primary-color); box-shadow: 0 0 0 2px var(--primary-light-color); }
.ts-form__composer {
  border: 1px solid var(--border-grey-color); border-radius: 8px; overflow: hidden;
  min-height: 220px; max-height: 40vh;
}
.ts-form__check {
  display: inline-flex; align-items: center; gap: 6px; margin-top: 8px;
  padding: 5px 10px; border: 1px solid var(--border-grey-color); border-radius: 16px;
  font-size: 0.8rem; color: var(--grey-color); cursor: pointer; align-self: flex-start;
}
.ts-form__check--on { color: var(--primary-color); border-color: var(--primary-color); background: var(--primary-light-color); }
.ts-form__checkbox { position: absolute; opacity: 0; width: 0; height: 0; }
.ts-form__error { color: var(--error-color); font-size: 0.82rem; margin: 4px 0 0; }
.ts-hint { font-size: 0.76rem; color: var(--grey-color); }
</style>
